import { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataBadge } from './components/DataBadge';
import { Box, Grid, Stack, SvgIcon, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { Environment } from '../../environment';
import { StarjackTable } from '@starjack/shared-components';
import { useLazyGetAggregatedTicketsQuery } from '../../liftoperator';
import {
    AggregatedTicket,
    AggregatedTicketSearchParams,
    getSumMoney,
} from '../../aggregatedTicket';
import { usePagination } from '@starjack/shared-components';
import { getMoneyAsString, addUpCosts } from '../../money';
import {
    ArrowTrendingUpIcon,
    ArrowUpOnSquareStackIcon,
    XMarkIcon,
} from '@heroicons/react/24/solid';
import XCircleIcon from '@heroicons/react/24/solid/XMarkIcon';
import { PieDiagram } from './components/PieDiagram';
import { LoadingButton } from '@mui/lab';
import ArrowDownOnSquareIcon from '@heroicons/react/24/solid/ArrowDownOnSquareIcon';
import { useExportAggregatedTickets } from '../lib/useExportAggregatedTickets';
import { difference } from 'lodash';
import { StatisticsFilter, type B2BUser } from './StatisticsFilter';

export const splitDiscountSales = (
    tickets: AggregatedTicket[]
): { labels: string[]; values: number[]; counts: number[] } => {
    const result = {
        labels: ['revenueWithoutDiscount', 'revenueWithDiscount'],
        values: [0, 0],
        counts: [0, 0],
    };

    return tickets.reduce((acc, ticket) => {
        if (ticket.conditions.length === 0) {
            acc.values[0] += ticket.sumSales.amount;
            acc.counts[0] += ticket.totalSales;
        } else {
            acc.values[1] += ticket.sumSales.amount;
            acc.counts[1] += ticket.totalSales;
        }

        return acc;
    }, result);
};

const getSales = (
    aggregatedTickets: AggregatedTicket[],
    propName: 'name' | 'conditions'
): { labels: string[]; values: number[]; counts: number[] } => {
    const tickets = aggregatedTickets || [];

    const aggregated = tickets.reduce<
        Record<string, { amount: number; count: number }>
    >((acc, ticket) => {
        const field = ticket[propName];
        const fieldValue = Array.isArray(field) ? field[0] : field;

        if (fieldValue === undefined) return acc;

        if (!acc[fieldValue]) {
            acc[fieldValue] = { amount: 0, count: 0 };
        }
        acc[fieldValue].amount += ticket.sumSales.amount;
        acc[fieldValue].count += ticket.totalSales;

        return acc;
    }, {});

    const sorted = Object.entries(aggregated).sort(
        (a, b) => b[1].amount - a[1].amount
    );

    const result: { labels: string[]; values: number[]; counts: number[] } = {
        labels: [],
        values: [],
        counts: [],
    };

    const itemsToShow = sorted.length <= 4 ? sorted : sorted.slice(0, 4);
    const restItems = sorted.length > 4 ? sorted.slice(4) : [];

    for (const [label, data] of itemsToShow) {
        result.labels.push(label);
        result.values.push(data.amount);
        result.counts.push(data.count);
    }

    if (restItems.length > 0) {
        const restAmount = restItems.reduce(
            (sum, [, data]) => sum + data.amount,
            0
        );
        const restCount = restItems.reduce(
            (sum, [, data]) => sum + data.count,
            0
        );
        result.labels.push('Sonstige');
        result.values.push(restAmount);
        result.counts.push(restCount);
    }

    return result;
};

type StatisticsDynamicPricingContentProps = {
    environments: Environment[];
    b2bUsers?: B2BUser[];
};

export const StatisticsDynamicPricingContent: FunctionComponent<
    StatisticsDynamicPricingContentProps
> = ({ environments, b2bUsers }) => {
    const theme = useTheme() as Theme & { page?: { verticalSpacing?: number } };
    const { t } = useTranslation();
    const [
        getAggregatedTicket,
        { data: aggregatedTickets = [], isUninitialized, isFetching },
    ] = useLazyGetAggregatedTicketsQuery();
    const [exportTickets, { isLoading: exportLoading }] =
        useExportAggregatedTickets();
    const [ticketSearch, setTicketSearch] =
        useState<AggregatedTicketSearchParams>({
            environments: environments,
        });

    if (difference(ticketSearch.environments ?? [], environments).length) {
        setTicketSearch({
            ...ticketSearch,
            environments: environments,
        });
    }

    const [showLoadingState, setShowLoadingState] = useState(true);
    const { currentItems, setNextPage, setItemsPerPage, itemsPerPage } =
        usePagination<AggregatedTicket>({
            items: aggregatedTickets,
            itemsPerPage: 10,
        });

    const sums = useMemo(
        () => ({
            sales: getMoneyAsString(
                addUpCosts(getSumMoney(aggregatedTickets, 'sumSales'))
            ),
            cancellations: getMoneyAsString(
                addUpCosts(getSumMoney(aggregatedTickets, 'sumCancellations'))
            ),
            deposits: getMoneyAsString(
                addUpCosts(getSumMoney(aggregatedTickets, 'sumDepositAmounts'))
            ),
            cancelledDeposits: getMoneyAsString(
                addUpCosts(
                    getSumMoney(aggregatedTickets, 'sumCancelledDepositAmounts')
                )
            ),
        }),
        [aggregatedTickets]
    );

    useEffect(() => {
        const loading = isFetching || isUninitialized;

        if (loading) {
            setShowLoadingState(loading);
        } else {
            setTimeout(() => {
                setShowLoadingState(false);
            }, 800);
        }
    }, [isFetching, isUninitialized]);

    useEffect(() => {
        getAggregatedTicket(ticketSearch);
    }, [ticketSearch]);

    const ticketSales = useMemo(
        () => getSales(aggregatedTickets, 'name'),
        [aggregatedTickets]
    );
    const personNameSales = useMemo(
        () => getSales(aggregatedTickets, 'conditions'),
        [aggregatedTickets]
    );

    const discountAmount = useMemo(() => {
        const result = splitDiscountSales(aggregatedTickets);
        return {
            ...result,
            labels: result.labels.map((label) => t(label)),
        };
    }, [aggregatedTickets, t]);

    if (isUninitialized) return null;

    const verticalSpacing = theme.page?.verticalSpacing ?? 3;

    return (
        <>
            <Stack
                sx={{
                    alignItems: 'flex-start',
                }}
                spacing={{ xs: 2, md: verticalSpacing }}
            >
                <StatisticsFilter
                    loading={showLoadingState}
                    b2bUsers={b2bUsers}
                    environments={environments}
                    onSearch={(params) => {
                        setTicketSearch({
                            environments: environments,
                            ...params,
                        });
                    }}
                />

                <LoadingButton
                    sx={{
                        mr: { xs: 0, md: 'auto' },
                        width: { xs: '100%', md: 'auto' },
                    }}
                    loading={exportLoading}
                    onClick={() => exportTickets(ticketSearch)}
                    color='inherit'
                    startIcon={
                        <SvgIcon fontSize='small'>
                            <ArrowDownOnSquareIcon />
                        </SvgIcon>
                    }
                >
                    {t('exportDetailedStatistics')}
                </LoadingButton>

                <Grid
                    sx={(th) => ({
                        ml: `-${th.spacing(3)} !important`,
                    })}
                    spacing={3}
                    container
                >
                    <Grid xs={12} sm={6} md={3} item>
                        <DataBadge
                            title={t('salesInEur')}
                            avatarColor={String(theme.palette.primary.main)}
                            avatarIcon={<ArrowTrendingUpIcon />}
                            data={{
                                value: String(sums.sales),
                            }}
                            loading={showLoadingState}
                        />
                    </Grid>

                    <Grid xs={12} sm={6} md={3} item>
                        <DataBadge
                            title={t('cancellationsInEur')}
                            avatarColor={String(theme.palette.error.main)}
                            avatarIcon={<XMarkIcon />}
                            data={{
                                value: String(sums.cancellations),
                            }}
                            loading={showLoadingState}
                        />
                    </Grid>

                    <Grid xs={12} sm={6} md={3} item>
                        <DataBadge
                            title={t('depositInEur')}
                            avatarColor={String(theme.palette.success.main)}
                            avatarIcon={<ArrowUpOnSquareStackIcon />}
                            data={{
                                value: String(sums.deposits),
                            }}
                            loading={showLoadingState}
                        />
                    </Grid>

                    <Grid xs={12} sm={6} md={3} item>
                        <DataBadge
                            title={t('depositCancelledInEur')}
                            avatarColor={String(theme.palette.secondary.main)}
                            avatarIcon={<XCircleIcon />}
                            data={{
                                value: String(sums.cancelledDeposits),
                            }}
                            loading={showLoadingState}
                        />
                    </Grid>
                </Grid>

                <Grid
                    sx={(th) => ({
                        ml: `-${th.spacing(3)} !important`,
                    })}
                    spacing={3}
                    container
                >
                    <Grid xs={12} md={4} item>
                        <PieDiagram
                            labels={ticketSales.labels}
                            chartSeries={ticketSales.values}
                            counts={ticketSales.counts}
                            title={t('salesByTicketName')}
                        />
                    </Grid>

                    <Grid xs={12} md={4} item>
                        <PieDiagram
                            labels={personNameSales.labels}
                            chartSeries={personNameSales.values}
                            counts={personNameSales.counts}
                            title={t('salesByPriceRules')}
                            subtitle={t('shareOfReducedTicketSales')}
                        />
                    </Grid>

                    <Grid xs={12} md={4} item>
                        <PieDiagram
                            labels={discountAmount.labels}
                            chartSeries={discountAmount.values}
                            counts={discountAmount.counts}
                            title={t('salesByPriceReduction')}
                        />
                    </Grid>
                </Grid>

                <StarjackTable<AggregatedTicket>
                    count={aggregatedTickets.length}
                    items={currentItems}
                    columns={[
                        {
                            label: t('ticketName'),
                            itemTransform: (ticket) => (
                                <TicketName ticket={ticket} />
                            ),
                        },
                        {
                            label: t('personType'),
                            itemKey: 'personName',
                        },

                        {
                            label: t('dynamicPricingRule'),
                            itemTransform: (ticket) => {
                                return ticket.conditions.length ? (
                                    <Typography>
                                        {ticket.conditions[0]}
                                    </Typography>
                                ) : (
                                    '-'
                                );
                            },
                        },

                        {
                            label: t('sales'),
                            itemKey: 'totalSales',
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },

                        {
                            label: t('salesPrice'),
                            itemTransform: (ticket) =>
                                getMoneyAsString(ticket.sumSales),
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },

                        {
                            label: t('originalPrice'),
                            itemTransform: (ticket) =>
                                getMoneyAsString(ticket.sumOriginalPriceSales),
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },

                        {
                            label: t('discount'),
                            itemTransform: (ticket) =>
                                getMoneyAsString({
                                    amount:
                                        ticket.sumOriginalPriceSales.amount -
                                        ticket.sumSales.amount,
                                    currency: 'EUR',
                                }),
                            options: {
                                headerCellProps: {
                                    align: 'right',
                                },
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },
                    ]}
                    rowsPerPage={itemsPerPage}
                    onRowsPerPageChange={setItemsPerPage}
                    onPageChange={(nextValue) => setNextPage(nextValue)}
                    loading={showLoadingState}
                />
            </Stack>
        </>
    );
};

const TicketName: FunctionComponent<{ ticket: AggregatedTicket }> = memo(
    ({ ticket }) => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
            }}
        >
            <Typography
                style={{
                    fontSize: '0.875rem',
                    lineHeight: '1.43',
                    letterSpacing: '0.01071em',
                }}
                gutterBottom
            >
                {ticket.name}
            </Typography>
        </Box>
    )
);

TicketName.displayName = 'TicketName';
